/**
 * useSupervisorHook manages supervisor ticket list state.
 *
 * Fetches the latest 500 matching tickets when filters or sort change. Pagination
 * within that window is done client-side by the consumer (e.g. Supervisor)
 * after optional filtering (e.g. by MSP customers). Page and pageSize from this hook are
 * for UI display only; changing them does not trigger a refetch.
 */
import { useCallback, useEffect, useState } from "react";

import { useAppDispatch, useAppSelector } from "./storeHook";
import { SupervisorAction } from "../actions";
import { useUserMsp } from "./useUserMsp";
import { isNinjaOne, isAutotask, isHalo } from "../utils/mspUtils";
import { useAuth0 } from "@auth0/auth0-react";

const FETCH_PAGE_SIZE = 500;
const DEFAULT_PAGE_SIZE = 10;

interface SupervisorHookOptions {
  skipListFetch?: boolean;
}

export function useSupervisorHook(
  initialFilters = {},
  initialPage = 1,
  options: SupervisorHookOptions = {}
) {
  const { skipListFetch = false } = options;
  const dispatch = useAppDispatch();
  const { user } = useAuth0();
  const {
    isLoading,
    tickets = [],
    totalCount,
    selectedTicket: selectedTicketFromState,
    error,
    ninjaoneUsers = [],
  } = useAppSelector((state) => state.supervisor);

  const { userMspInfo } = useUserMsp();

  // Normalized filters object
  const [filters, setFilters] = useState<{
    groupName?: string;
    categoryName?: string[];
    status?: string[];
    customer?: string[];
    boardId?: number;
    includeAllStatuses?: boolean;
    refreshToken?: string;
  }>({ ...{ groupName: "", status: [], customer: [] }, ...initialFilters });

  const [page, setPage] = useState<number>(initialPage);
  const [pageSize, setPageSize] = useState<number>(DEFAULT_PAGE_SIZE);

  const [sort, setSort] = useState<
    { attribute: string; order: "ASC" | "DESC" }[]
  >([{ attribute: "created_at", order: "DESC" }]);

  const [selectedTicketId, setSelectedTicketId] = useState<string | null>(null);

  useEffect(() => {
    if (userMspInfo?.msp_type && isNinjaOne(userMspInfo.msp_type)) {
      dispatch(SupervisorAction.getNinjaoneUsers());
    }
  }, [userMspInfo?.msp_type, dispatch]);

  useEffect(() => {
    if (skipListFetch) return;
    if (!userMspInfo?.msp_id) return;
    if (selectedTicketId) return;

    const load = (silent = false) => {
      const readFilters = silent ? { ...filters, refreshToken: undefined } : filters;
    if (userMspInfo?.msp_type && isNinjaOne(userMspInfo.msp_type)) {
      dispatch(
        SupervisorAction.getNinjaoneTickets(1, FETCH_PAGE_SIZE, readFilters, sort, { silent })
      );
    } else if (userMspInfo?.msp_type && isAutotask(userMspInfo.msp_type)) {
      dispatch(
        SupervisorAction.getAutotaskTickets(1, FETCH_PAGE_SIZE, readFilters, sort, { silent })
      );
    } else if (userMspInfo?.msp_type && isHalo(userMspInfo.msp_type)) {
      dispatch(
        SupervisorAction.getHaloTickets(1, FETCH_PAGE_SIZE, readFilters, sort, { silent })
      );
    } else {
      dispatch(
        SupervisorAction.getSuperopsTickets(1, FETCH_PAGE_SIZE, readFilters, sort, { silent })
      );
    }
    };
    load();
    let lastRefreshAt = 0;
    const refresh = () => {
      const now = Date.now();
      if (document.visibilityState !== "visible" || now - lastRefreshAt < 1000) return;
      lastRefreshAt = now;
      load(true);
    };
    const interval = window.setInterval(refresh, 60_000);
    window.addEventListener("focus", refresh);
    document.addEventListener("visibilitychange", refresh);
    return () => {
      window.clearInterval(interval);
      window.removeEventListener("focus", refresh);
      document.removeEventListener("visibilitychange", refresh);
      SupervisorAction.cancelListReads();
    };
  }, [
    filters,
    sort,
    userMspInfo?.msp_type,
    userMspInfo?.msp_id,
    dispatch,
    selectedTicketId,
    skipListFetch,
  ]);

  useEffect(() => {
    setPage(1);
  }, [filters, sort]);

  useEffect(() => {
    if (!selectedTicketId || !user?.email) return;
    dispatch(SupervisorAction.getTicketById(selectedTicketId, user.email));
    let lastRefreshAt = 0;
    const refresh = () => {
      const now = Date.now();
      if (document.visibilityState !== "visible" || now - lastRefreshAt < 1000) return;
      lastRefreshAt = now;
      dispatch(SupervisorAction.refreshTicketById(selectedTicketId, user.email, { background: true }));
    };
    const interval = window.setInterval(refresh, 60_000);
    window.addEventListener("focus", refresh);
    document.addEventListener("visibilitychange", refresh);
    return () => {
      window.clearInterval(interval);
      window.removeEventListener("focus", refresh);
      document.removeEventListener("visibilitychange", refresh);
      SupervisorAction.cancelDetailReads();
    };
  }, [selectedTicketId, user?.email, dispatch]);

  const handleTicketSelect = useCallback((ticketId: string) => {
    setSelectedTicketId(ticketId);
  }, []);

  const handleBackToList = () => {
    setSelectedTicketId(null);
  };

  const handlePageChange = (
    event: React.ChangeEvent<unknown>,
    value: number
  ) => {
    setPage(value);
  };

  const handlePageSizeChange = (nextPageSize: number) => {
    if (!Number.isFinite(nextPageSize) || nextPageSize <= 0) {
      return;
    }
    setPage(1);
    setPageSize(nextPageSize);
  };

  const assistantTicketDisplayId = selectedTicketFromState
    ? selectedTicketFromState.displayId || selectedTicketFromState.id
    : null;

  return {
    isLoading,
    tickets,
    error,
    totalCount,
    page,
    setPage,
    pageSize,
    setPageSize,
    filters,
    setFilters,
    selectedTicket: selectedTicketFromState,
    handleTicketSelect,
    handleBackToList,
    handlePageChange,
    handlePageSizeChange,
    selectedTicketId,
    assistantTicketDisplayId,
    sort,
    setSort,
    ninjaoneUsers,
  };
}
